// The special forms, builtins and standard library, for autocompletion. The help panel lists the
// same names.

export interface Word {
  name: string;
  info: string;
  /** Keyword parameters and their defaults, offered as :keys inside a call. */
  keys?: [string, string][];
}

/** What every standard library shape passes on to fire through & opts. */
const passed: [string, string][] = [
  ['run', 'nil'],
  ['tags', 'nil'],
  ['delay', '0'],
  ['cancellable', 'true'],
  ['at', '[0 0]'],
  ['attach', 'nil'],
  ['motion', "'linear"],
  ['spin', '0'],
  ['amp', '0'],
  ['period', '60'],
];

const placement: [string, string][] = [
  ['at', '[0 0]'],
  ['tags', 'nil'],
  ['delay', '0'],
  ['cancellable', 'true'],
];

export const specialForms: Word[] = [
  { name: 'def', info: '(def name value)' },
  { name: 'defn', info: '(defn name [a b :key default ... & opts] body...)' },
  { name: 'fn', info: '(fn [a :key default ... & opts] body...)' },
  { name: 'let', info: '(let [a 1 [x y] pos] body...)' },
  { name: 'if', info: '(if test then else)' },
  { name: 'when', info: '(when test body...)' },
  { name: 'cond', info: '(cond test value test value ...)' },
  { name: 'do', info: '(do body...)' },
  { name: 'and', info: '(and a b ...)' },
  { name: 'or', info: '(or a b ...)' },
  { name: 'dotimes', info: '(dotimes [i n] body...)' },
  { name: 'doseq', info: '(doseq [x xs] body...) or (doseq [[a b] rows] ...), may wait' },
  { name: 'forever', info: '(forever body...)' },
  { name: 'loop', info: '(loop [a 0] body... (recur ...))' },
  { name: 'recur', info: '(recur new-values...)' },
  { name: 'par', info: '(par a b ...) runs them together; ends when all end' },
  { name: 'race', info: '(race a b ...) ends when the first ends' },
  { name: 'until', info: '(until test body...) cancels the body when test is true' },
  { name: 'wait-until', info: '(wait-until test)' },
  { name: 'quote', info: "(quote name), the same as 'name" },
  { name: 'mirror', info: '(mirror body...) flips what the body fires across the vertical' },
  { name: 'rotate', info: '(rotate deg body...) turns what the body fires' },
  { name: 'offset', info: '(offset x y body...) fires from an offset' },
  { name: 'speed-scale', info: '(speed-scale k body...) scales the speeds the body uses' },
];

export const builtins: Word[] = [
  {
    name: 'fire',
    info: "(fire :dir :speed :kind :run :at [x y] :tags :attach :motion 'polar|'wave :spin :amp :period :delay :cancellable)",
    keys: [
      ['dir', '(aim)'],
      ['speed', '1'],
      ['kind', "'bullet"],
      ['run', 'nil'],
      ['attach', 'nil'],
      ['motion', 'nil'],
      ['spin', '0'],
      ['amp', '0'],
      ['period', '60'],
      ...placement,
    ],
  },
  {
    name: 'cancel',
    info: '(cancel :tags [...]) removes cancellable bullets; the host turns them into items',
    keys: [['tags', 'every cancellable bullet']],
  },
  { name: 'move-to', info: '(move-to x y :over ticks) in world units', keys: [['over', '0']] },
  {
    name: 'wait-beats',
    info: "(wait-beats n) waits for a beat on the host's beat clock; (wait-beats n t) waits t ticks without one",
  },
  { name: 'beat', info: '(beat) the beat now, from the settings menu; (beat d) is d without a clock' },
  { name: 'by-diff', info: '(by-diff easy normal hard lunatic) picks by difficulty' },
  { name: 'target-x', info: '(target-x) or (target-x i)' },
  { name: 'target-y', info: '(target-y) or (target-y i)' },
  { name: 'target-count', info: '(target-count)' },
  { name: 'drift-x', info: '(drift-x) extra velocity from accel' },
  { name: 'drift-y', info: '(drift-y) extra velocity from accel' },
  { name: 'wait', info: '(wait ticks), fractions allowed' },
  { name: 'vanish', info: '(vanish) removes this actor' },
  { name: 'emit', info: "(emit 'name) sends an event to the host" },
  { name: 'change-speed', info: '(change-speed v :over ticks)', keys: [['over', '0']] },
  { name: 'change-dir', info: '(change-dir degrees :over ticks)', keys: [['over', '0']] },
  { name: 'turn', info: '(turn degrees-per-tick :for ticks)', keys: [['for', 'forever']] },
  { name: 'accel', info: '(accel ax ay :over ticks)', keys: [['over', '0']] },
  {
    name: 'aim',
    info: '(aim), (aim :target i), (aim :lead speed)',
    keys: [
      ['target', 'nearest'],
      ['lead', 'none'],
    ],
  },
  { name: 'heading', info: '(heading)' },
  { name: 'speed', info: '(speed)' },
  { name: 'x', info: '(x)' },
  { name: 'y', info: '(y)' },
  { name: 'age', info: '(age) in ticks' },
  { name: 'last-dir', info: '(last-dir) of the last shot, or (last-dir d) with a default' },
  { name: 'last-speed', info: '(last-speed) of the last shot, or (last-speed s) with a default' },
  { name: 'set-last-dir', info: '(set-last-dir d) what (last-dir) reads next, without firing' },
  { name: 'set-last-speed', info: '(set-last-speed s) what (last-speed) reads next, without firing' },
  { name: 'tick', info: '(tick) the tick this code runs in' },
  {
    name: 'laser',
    info: "(laser :dir :length 512 :width 8 :warn 30 :active 60 :fade 15 :turn 0 :kind 'laser :attach true)",
    keys: [
      ['dir', '(aim)'],
      ['length', '512'],
      ['width', '8'],
      ['warn', '30'],
      ['active', '60'],
      ['fade', '15'],
      ['turn', '0'],
      ['kind', "'laser"],
      ['attach', 'true'],
      ...placement,
    ],
  },
  {
    name: 'curvy-laser',
    info: "(curvy-laser :dir :curve 'head|'wave|'polar :speed 3 :turn :amp 24 :period 60 :spin 2 :segments 24 :window 40 :width 6 :warn 0 :active 180 :fade 20 :kind :attach true)",
    keys: [
      ['dir', '(aim)'],
      ['curve', "'head"],
      ['speed', '3'],
      ['turn', '0'],
      ['amp', '24'],
      ['period', '60'],
      ['spin', '2'],
      ['segments', '24'],
      ['window', '40'],
      ['width', '6'],
      ['warn', '0'],
      ['active', '180'],
      ['fade', '20'],
      ['kind', "'laser"],
      ['attach', 'true'],
      ...placement,
    ],
  },
  { name: 'list', info: '(list a ...) or [a ...]' },
  { name: 'nth', info: '(nth xs i)' },
  { name: 'count', info: '(count xs)' },
  { name: 'first', info: '(first xs), nil if empty' },
  { name: 'rest', info: '(rest xs)' },
  { name: 'last', info: '(last xs), nil if empty' },
  { name: 'conj', info: '(conj xs a ...)' },
  { name: 'concat', info: '(concat xs ...)' },
  { name: 'reverse', info: '(reverse xs)' },
  { name: 'empty?', info: '(empty? xs)' },
  { name: 'range', info: '(range end), (range start end), (range start end step)' },
  { name: 'map', info: '(map f xs), f takes one argument and may not wait' },
  { name: 'filter', info: '(filter f xs)' },
  { name: 'reduce', info: '(reduce f xs) or (reduce f init xs)' },
  { name: 'rand-nth', info: '(rand-nth xs) a random item' },
  // The standard library, loaded into every VM. Programs may redefine any of them.
  {
    name: 'ring',
    info: "(ring n :dir 0 :speed 2 :kind 'bullet & opts) n shots round a full circle",
    keys: [['dir', '0'], ['speed', '2'], ['kind', "'bullet"], ...passed],
  },
  {
    name: 'fan',
    info: "(fan n :dir (aim) :step 15 :speed 2 :kind 'bullet & opts) n shots :step degrees apart",
    keys: [['dir', '(aim)'], ['step', '15'], ['speed', '2'], ['kind', "'bullet"], ...passed],
  },
  {
    name: 'spread',
    info: "(spread n :dir (aim) :width 60 :speed 2 :kind 'bullet & opts) n shots across an arc",
    keys: [['dir', '(aim)'], ['width', '60'], ['speed', '2'], ['kind', "'bullet"], ...passed],
  },
  {
    name: 'polygon',
    info: "(polygon sides :per-side 5 :dir 90 :speed 2 :kind 'bullet & opts) a polygon outline",
    keys: [['per-side', '5'], ['dir', '90'], ['speed', '2'], ['kind', "'bullet"], ...passed],
  },
  {
    name: 'rose',
    info: "(rose n :petals 4 :dir 0 :speed 2 :kind 'bullet & opts) shots that open into petals",
    keys: [['petals', '4'], ['dir', '0'], ['speed', '2'], ['kind', "'bullet"], ...passed],
  },
  {
    name: 'stream',
    info: "(stream n :dir (aim) :speed 2 :gap 4 :kind 'bullet & opts) n shots, :gap ticks apart",
    keys: [['dir', '(aim)'], ['speed', '2'], ['gap', '4'], ['kind', "'bullet"], ...passed],
  },
  {
    name: 'burst',
    info: "(burst n :dir (aim) :width 60 :speed [1 3] :kind 'bullet & opts) random shots in an arc",
    keys: [['dir', '(aim)'], ['width', '60'], ['speed', '[1 3]'], ['kind', "'bullet"], ...passed],
  },
  { name: 'rand', info: '(rand) or (rand a b)' },
  { name: 'rank', info: 'difficulty from the host, 0 to 1' },
  { name: 'input', info: "(input 'name default), e.g. 'var1 or 'field-w" },
  ...['+', '-', '*', '/', 'mod', 'inc', 'dec', 'abs', 'min', 'max'].map((name) => ({ name, info: 'arithmetic' })),
  ...['floor', 'ceil', 'round', 'trunc', 'sqrt', 'pow'].map((name) => ({ name, info: 'maths' })),
  ...['sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2'].map((name) => ({ name, info: 'degrees' })),
  { name: 'lerp', info: '(lerp a b t)' },
  { name: 'clamp', info: '(clamp v lo hi)' },
  ...['<', '>', '<=', '>=', '=', 'not=', 'not'].map((name) => ({ name, info: 'comparison' })),
];
